import { Head, Link } from "@inertiajs/react"

import AppLogoIcon from "@/components/app-logo-icon"
import { Button } from "@/components/ui/button"
import { rootPath } from "@/routes"

interface ErrorPageProps {
  status: number
}

const errorConfig: Record<
  number,
  { title: string; headline: string; message: string; cta: string }
> = {
  400: {
    title: "Bad request",
    headline: "WE DON'T SPEAK GIBBERISH",
    message:
      "Whatever you just sent us, we couldn't make sense of it. The request was malformed, corrupted, or just plain confusing.",
    cta: "Back to safety",
  },
  403: {
    title: "Access denied",
    headline: "YOU'RE NOT ON THE LIST",
    message:
      "This area is off-limits. Either you don't have permission, or you're trying to sneak into someone else's graveyard.",
    cta: "Back to your turf",
  },
  404: {
    title: "Page not found",
    headline: "THIS PAGE HAS FLATLINED",
    message:
      "The page you're looking for either never existed, got completed and moved on, or died waiting for someone to find it.",
    cta: "Back to the living",
  },
  406: {
    title: "Not acceptable",
    headline: "YOUR BROWSER IS ANCIENT HISTORY",
    message:
      "We can't let you in with that browser—it's practically a fossil. Upgrade to something from this decade.",
    cta: "Try the homepage",
  },
  422: {
    title: "Unprocessable",
    headline: "DEAD ON ARRIVAL",
    message:
      "Whatever you just tried, we couldn't process it. Maybe you tried to change something you don't have permission for.",
    cta: "Start fresh",
  },
  500: {
    title: "Server error",
    headline: "SOMETHING DIED ON OUR END",
    message:
      "Our servers just experienced their own deadline. We're working to bring them back from the dead. This one's on us.",
    cta: "Try the homepage",
  },
  503: {
    title: "Service unavailable",
    headline: "WE'RE TAKING A DIRT NAP",
    message:
      "The service is temporarily down for maintenance. We'll be back from the dead shortly.",
    cta: "Try again later",
  },
}

const defaultError = {
  title: "Something went wrong",
  headline: "UNEXPECTED DEATH",
  message:
    "Something went wrong that we didn't anticipate. Give it another shot.",
  cta: "Back to safety",
}

export default function ErrorPage({ status }: ErrorPageProps) {
  const config = errorConfig[status] || defaultError

  return (
    <>
      <Head title={`${status} - ${config.title} | ToDoOrDie`} />

      <div className="vignette noise bg-bg-body text-bone flex min-h-screen items-center justify-center">
        <main className="grid max-w-lg gap-8 p-8 text-center">
          {/* Skull Icon */}
          <div className="flex justify-center">
            <div className="text-bone-muted">
              <svg
                className="h-20 w-20 opacity-90"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m12.5 17-.5-1-.5 1h1z" />
                <path d="M15 22a1 1 0 0 0 1-1v-1a2 2 0 0 0 1.56-3.25 8 8 0 1 0-11.12 0A2 2 0 0 0 8 20v1a1 1 0 0 0 1 1z" />
                <circle cx="15" cy="12" r="1" />
                <circle cx="9" cy="12" r="1" />
              </svg>
            </div>
          </div>

          {/* Error Code */}
          <div className="text-red-bright font-display text-[clamp(6rem,20vw,10rem)] leading-none tracking-wide [text-shadow:0_0_60px_var(--red-glow)]">
            {status}
          </div>

          {/* Animated flatline for 500 errors */}
          {status === 500 && (
            <svg
              className="mx-auto -my-2 h-[60px] w-full max-w-[280px]"
              viewBox="0 0 280 60"
              preserveAspectRatio="xMidYMid meet"
            >
              <path
                className="stroke-red-bright animate-[flatline_3s_ease-out_infinite] fill-none [stroke-width:2] [filter:drop-shadow(0_0_8px_var(--red-glow))] [stroke-dasharray:400] [stroke-linecap:round] [stroke-linejoin:round]"
                d="M0,30 L60,30 L70,30 L80,10 L90,50 L100,20 L110,40 L120,30 L140,30 L280,30"
              />
            </svg>
          )}

          {/* Headline */}
          <h1 className="font-display text-bone text-[clamp(1.5rem,5vw,2rem)] tracking-[0.1em]">
            {config.headline}
          </h1>

          {/* Message */}
          <p className="text-bone-muted max-w-md text-[0.95rem]">
            {config.message}
          </p>

          {/* CTA Button */}
          <Button asChild className="mx-auto mt-2">
            <Link href={rootPath()}>{config.cta}</Link>
          </Button>

          {/* Subtext */}
          <p className="text-bone-dim mt-4 text-xs">
            {status === 404 && "Unlike your tasks, this one's not coming back."}
            {status === 500 &&
              "Even we can't cheat death forever. Give it a minute."}
            {status === 422 &&
              "Some things can't be saved. Go back and try again."}
            {status === 400 && "Garbage in, error out."}
            {status === 403 && "Nice try, but no."}
            {status === 503 && "Patience is a virtue. So is uptime."}
            {!errorConfig[status] && "Well, that wasn't supposed to happen."}
          </p>

          {/* Footer */}
          <div className="text-bone-dim mt-8 flex items-center justify-center gap-2 text-sm">
            <AppLogoIcon className="text-red h-5 w-5" />
            ToDoOrDie
          </div>
        </main>
      </div>
    </>
  )
}
